import { components as c } from "@/components";
import { formComponents as fc } from "@/features/i18n/formComponents";
import EducationFormFields from "@/features/events/features/educations/EducationFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useEducationMutation } from "@/features/events/features/educations/api/upsertEducation";
import { diaryByIdQueryOptions, useDiaryByIdQuery } from "@/features/diaries";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const searchSchema = updateDiaryFlowSearchSchema.extend({
    event_type: z.literal("education"),
});

const educationFormSchema = z.object({
    education_id: z.string().uuid(),
    event_id: z.string().uuid(),
    education_name: z.string().min(1, "Please provide a name for this consultation"),
    education_description: z.string().optional().default(""),
    educated_by: z.string().uuid("Please select who provided the consultation"),
});

type EducationFormValues = z.infer<typeof educationFormSchema>;

const AddEducationPage = () => {
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const navigate = useNavigate();

    // Generate education ID
    const educationId = crypto.randomUUID();

    const { mutate: upsertEducation } = useEducationMutation({ diaryId });

    const form = useForm<EducationFormValues>({
        resolver: zodResolver(educationFormSchema),
        defaultValues: {
            event_id: eventId,
            education_id: educationId,
            education_name: search.education_name ?? "",
            education_description: search.education_description ?? "",
            educated_by: search.educated_by ?? undefined,
        },
    });

    const handleSubmit = async (formData: EducationFormValues) => {
        // First, ensure the event exists
        await insertEvent({ event_id: eventId, diary_id: diaryId });
        // Then insert the education
        upsertEducation(formData, {
            onSuccess: () => {
                void navigate({
                    to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                    params: { diaryId, eventId, trackerId },
                    search: { ...search, type },
                });
            },
        });
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <h2 className="font-semibold text-lg">Consultation</h2>
                <EducationFormFields organisationId={diary?.organisation_id as string} />
                <fc.NextButton buttonType="submit" size="lg" className="w-full" />
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute(
    "/_app/patients/$diaryId/update/$eventId/$trackerId/intervention-details/education"
)({
    component: AddEducationPage,
    validateSearch: searchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        return { diary };
    },
});
